import React, { useEffect, useState } from 'react';
import { 
  Card, 
  Button, 
  message, 
  Spin, 
  Typography, 
  Modal, 
  Form, 
  Input, 
  Select,
  Space,
  Popconfirm,
  Tabs,
  Badge,
  Tooltip,
  Row,
  Col,
  Tag,
  Divider,
  Empty,
  Switch,
  InputNumber
} from 'antd';
import { 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  SettingOutlined,
  DatabaseOutlined,
  CloudOutlined,
  SafetyCertificateOutlined,
  GlobalOutlined,
  FileTextOutlined,
  SaveOutlined,
  EyeOutlined,
  CopyOutlined
} from '@ant-design/icons';
import { PERMISSIONS } from '../../component/permissions';
import { useAuth } from '../../component/AuthContext';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;
const { TabPane } = Tabs;

interface ConfigItem {
  section: string;
  key: string;
  value: any;
  type: 'object' | 'string' | 'number' | 'boolean' | 'array';
}

interface ConfigSection {
  name: string;
  items: ConfigItem[];
  icon: React.ReactNode;
  description: string;
}

const getSectionIcon = (sectionName: string): React.ReactNode => {
  const name = sectionName.toLowerCase();
  if (name.includes('connection') || name.includes('database')) return <DatabaseOutlined />;
  if (name.includes('remote')) return <GlobalOutlined />;
  if (name.includes('ftp')) return <CloudOutlined />;
  if (name.includes('azure')) return <CloudOutlined />;
  if (name.includes('production')) return <SafetyCertificateOutlined />;
  return <SettingOutlined />;
};

const getSectionDescription = (sectionName: string): string => {
  const descriptions: Record<string, string> = {
    postgresql_connections: 'Database connection configurations for different environments',
    remote_connections: 'Remote server connection settings and authentication',
    ftp_connections: 'FTP server configurations for file transfers',
    production_image_servers: 'Production image server endpoints',
    azure: 'Azure cloud storage configuration',
    TRAIN_WORKER_URLS: 'Training worker service endpoints',
  };
  return descriptions[sectionName] || 'Configuration settings';
};

const getTypeColor = (type: string): string => {
  const colors: Record<string, string> = {
    string: 'blue',
    number: 'green',
    boolean: 'orange',
    object: 'purple',
    array: 'cyan',
  };
  return colors[type] || 'default';
};

const ConfigManagementPage: React.FC = () => {
  const { canWrite } = useAuth();
  const [config, setConfig] = useState<Record<string, any>>({});
  const [configSections, setConfigSections] = useState<ConfigSection[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [editingItem, setEditingItem] = useState<ConfigItem | null>(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isAddModalVisible, setIsAddModalVisible] = useState(false);
  const [isViewModalVisible, setIsViewModalVisible] = useState(false);
  const [viewingItem, setViewingItem] = useState<ConfigItem | null>(null);
  const [activeSection, setActiveSection] = useState<string>('');
  const [form] = Form.useForm();
  const [addForm] = Form.useForm();

  const fetchConfig = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/config`, {
        credentials: 'include',
      });
      if (!res.ok) {
        throw new Error('Failed to load configuration');
      }
      const data = await res.json();
      setConfig(data.config);
      processConfigIntoSections(data.config);
    } catch (err: any) {
      message.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const processConfigIntoSections = (configData: Record<string, any>) => {
    const sections: ConfigSection[] = [];
    
    Object.entries(configData).forEach(([sectionName, sectionData]) => {
      const items: ConfigItem[] = [];
      
      if (typeof sectionData === 'object' && sectionData !== null) {
        Object.entries(sectionData).forEach(([key, value]) => {
          let type: ConfigItem['type'] = 'string';
          if (Array.isArray(value)) type = 'array';
          else if (typeof value === 'object' && value !== null) type = 'object';
          else if (typeof value === 'number') type = 'number';
          else if (typeof value === 'boolean') type = 'boolean';

          items.push({
            section: sectionName,
            key,
            value,
            type
          });
        });
      }
      
      sections.push({
        name: sectionName,
        items,
        icon: getSectionIcon(sectionName),
        description: getSectionDescription(sectionName)
      });
    });
    
    setConfigSections(sections);
    if (sections.length > 0 && !activeSection) {
      setActiveSection(sections[0].name);
    }
  };

  useEffect(() => {
    if (canWrite(PERMISSIONS.SYSTEM_MANAGEMENT)) {
      fetchConfig();
    } else {
      message.error("You don't have permission to manage configuration.");
      setLoading(false);
    }
  }, [canWrite]);

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '60vh',
        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'
      }}>
        <Card style={{ textAlign: 'center', border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>
            <Text style={{ fontSize: 16, color: '#666' }}>Loading configuration...</Text>
          </div>
        </Card>
      </div>
    );
  }

  if (!canWrite(PERMISSIONS.SYSTEM_MANAGEMENT)) {
    return (
      <div style={{ 
        padding: 40, 
        textAlign: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        minHeight: '60vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <Card style={{ maxWidth: 400, boxShadow: '0 8px 32px rgba(0,0,0,0.1)' }}>
          <SafetyCertificateOutlined style={{ fontSize: 48, color: '#ff4d4f', marginBottom: 16 }} />
          <Title level={3} style={{ color: '#ff4d4f' }}>Access Denied</Title>
          <Paragraph style={{ color: '#666', marginBottom: 0 }}>
            You don't have permission to manage system configuration.
          </Paragraph>
        </Card>
      </div>
    );
  }

  const saveConfigItem = async (item: ConfigItem, newValue: any) => {
    const saveKey = `${item.section}.${item.key}`;
    setSavingKey(saveKey);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/config/${item.section}/${item.key}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(newValue),
        }
      );
      if (!res.ok) throw new Error('Update failed');
      
      message.success(`Successfully updated ${saveKey}`);
      await fetchConfig();
    } catch (err: any) {
      message.error(err.message);
    } finally {
      setSavingKey(null);
    }
  };

  const deleteConfigItem = async (item: ConfigItem) => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/config/${item.section}/${item.key}`,
        {
          method: 'DELETE',
          credentials: 'include',
        }
      );
      if (!res.ok) throw new Error('Delete failed');
      
      message.success(`Successfully deleted ${item.section}.${item.key}`);
      await fetchConfig();
    } catch (err: any) {
      message.error(err.message);
    }
  };

  const addConfigItem = async (section: string, key: string, value: any) => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/config/${section}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ key, value }),
        }
      );
      if (!res.ok) throw new Error('Add failed');
      
      message.success(`Successfully added ${section}.${key}`);
      await fetchConfig();
    } catch (err: any) {
      message.error(err.message);
    }
  };

  const handleView = (item: ConfigItem) => {
    setViewingItem(item);
    setIsViewModalVisible(true);
  };

  const handleEdit = (item: ConfigItem) => {
    setEditingItem(item);
    const formValue = item.type === 'object' || item.type === 'array' 
      ? JSON.stringify(item.value, null, 2) 
      : item.value;
    form.setFieldsValue({ value: formValue });
    setIsModalVisible(true);
  };

  const handleSaveEdit = async () => {
    try {
      const values = await form.validateFields();
      let parsedValue = values.value;
      
      if (editingItem?.type === 'object' || editingItem?.type === 'array') {
        try {
          parsedValue = JSON.parse(values.value);
        } catch (e) {
          message.error('Invalid JSON format');
          return;
        }
      } else if (editingItem?.type === 'number') {
        parsedValue = Number(values.value);
      } else if (editingItem?.type === 'boolean') {
        parsedValue = values.value === true || values.value === 'true';
      }
      
      if (editingItem) {
        await saveConfigItem(editingItem, parsedValue);
      }
      
      setIsModalVisible(false);
      setEditingItem(null);
      form.resetFields();
    } catch (error) {
      console.error('Validation failed:', error);
    }
  };

  const handleAdd = async () => {
    try {
      const values = await addForm.validateFields();
      let parsedValue = values.value;
      
      if (values.type === 'object' || values.type === 'array') {
        try {
          parsedValue = JSON.parse(values.value);
        } catch (e) {
          message.error('Invalid JSON format');
          return;
        }
      } else if (values.type === 'number') {
        parsedValue = Number(values.value);
      } else if (values.type === 'boolean') {
        parsedValue = values.value === true || values.value === 'true';
      }
      
      await addConfigItem(activeSection, values.key, parsedValue);
      
      setIsAddModalVisible(false);
      addForm.resetFields();
    } catch (error) {
      console.error('Validation failed:', error);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    message.success('Copied to clipboard');
  };

  const renderConfigCard = (item: ConfigItem) => {
    const isComplex = item.type === 'object' || item.type === 'array';
    const displayValue = isComplex 
      ? `${Array.isArray(item.value) ? 'Array' : 'Object'} (${Object.keys(item.value || {}).length} ${Array.isArray(item.value) ? 'items' : 'properties'})`
      : String(item.value);

    return (
      <Card
        key={`${item.section}-${item.key}`}
        size="small"
        style={{ 
          marginBottom: 12,
          border: '1px solid #f0f0f0',
          borderRadius: 8,
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
          transition: 'all 0.3s ease'
        }}
        bodyStyle={{ padding: 16 }}
        hoverable
      >
        <Row align="middle" justify="space-between">
          <Col flex={1}>
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              <Space align="center">
                <Text strong style={{ fontSize: 16, color: '#1890ff' }}>
                  {item.key}
                </Text>
                <Tag color={getTypeColor(item.type)} style={{ borderRadius: 4 }}>
                  {item.type}
                </Tag>
              </Space>
              <div style={{ 
                background: '#fafafa', 
                padding: '8px 12px', 
                borderRadius: 6,
                border: '1px solid #f0f0f0',
                fontSize: 13,
                fontFamily: 'Monaco, Consolas, monospace',
                color: '#666',
                wordBreak: 'break-all',
                maxHeight: '60px',
                overflow: 'hidden'
              }}>
                {displayValue}
              </div>
            </Space>
          </Col>
          <Col>
            <Space>
              {isComplex && (
                <Tooltip title="View full content">
                  <Button 
                    type="text" 
                    icon={<EyeOutlined />} 
                    size="small"
                    onClick={() => handleView(item)}
                    style={{ color: '#1890ff' }}
                  />
                </Tooltip>
              )}
              <Tooltip title="Copy value">
                <Button 
                  type="text" 
                  icon={<CopyOutlined />} 
                  size="small"
                  onClick={() => copyToClipboard(isComplex ? JSON.stringify(item.value, null, 2) : String(item.value))}
                  style={{ color: '#52c41a' }}
                />
              </Tooltip>
              <Tooltip title="Edit">
                <Button 
                  type="text" 
                  icon={<EditOutlined />} 
                  size="small"
                  onClick={() => handleEdit(item)}
                  style={{ color: '#faad14' }}
                />
              </Tooltip>
              <Popconfirm
                title="Delete this configuration item?"
                description="This action cannot be undone."
                onConfirm={() => deleteConfigItem(item)}
                okText="Yes, delete"
                cancelText="Cancel"
                okButtonProps={{ danger: true }}
              >
                <Tooltip title="Delete">
                  <Button 
                    type="text" 
                    danger 
                    icon={<DeleteOutlined />} 
                    size="small"
                  />
                </Tooltip>
              </Popconfirm>
            </Space>
          </Col>
        </Row>
      </Card>
    );
  };

  const currentSection = configSections.find(section => section.name === activeSection);

  return (
    <div style={{ 
      padding: '24px 24px 0', 
      background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
      minHeight: '100vh'
    }}>
      {/* Header */}
      <Card style={{ marginBottom: 24, border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
        <Row align="middle" justify="space-between">
          <Col>
            <Space align="center">
              <SettingOutlined style={{ fontSize: 28, color: '#1890ff' }} />
              <div>
                <Title level={2} style={{ margin: 0, color: '#1890ff' }}>
                  Configuration Management
                </Title>
                <Text type="secondary" style={{ fontSize: 14 }}>
                  Manage your application configuration settings
                </Text>
              </div>
            </Space>
          </Col>
          <Col>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              size="large"
              onClick={() => setIsAddModalVisible(true)}
              disabled={!activeSection}
              style={{ 
                borderRadius: 8,
                height: 40,
                paddingLeft: 20,
                paddingRight: 20
              }}
            >
              Add Configuration
            </Button>
          </Col>
        </Row>
      </Card>

      {/* Main Content */}
      <Card 
        style={{ 
          border: 'none', 
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          borderRadius: 12
        }}
        bodyStyle={{ padding: 0 }}
      >
        <Tabs 
          activeKey={activeSection}
          onChange={setActiveSection}
          type="card"
          size="large"
          style={{ margin: 0 }}
          tabBarStyle={{ 
            margin: 0, 
            paddingLeft: 24, 
            paddingRight: 24,
            background: '#fafafa',
            borderRadius: '12px 12px 0 0'
          }}
        >
          {configSections.map(section => (
            <TabPane 
              tab={
                <Space>
                  {section.icon}
                  <span>{section.name.replace(/_/g, ' ').toUpperCase()}</span>
                  <Badge count={section.items.length} style={{ backgroundColor: '#1890ff' }} />
                </Space>
              } 
              key={section.name}
            >
              <div style={{ padding: 24 }}>
                <div style={{ marginBottom: 20 }}>
                  <Text type="secondary" style={{ fontSize: 14 }}>
                    {section.description}
                  </Text>
                </div>
                
                {section.items.length === 0 ? (
                  <Empty 
                    description="No configuration items in this section"
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    style={{ margin: '60px 0' }}
                  >
                    <Button 
                      type="primary" 
                      icon={<PlusOutlined />}
                      onClick={() => setIsAddModalVisible(true)}
                    >
                      Add First Item
                    </Button>
                  </Empty>
                ) : (
                  <div style={{ 
                    display: 'grid', 
                    gap: 16,
                    gridTemplateColumns: 'repeat(auto-fill, minmax(500px, 1fr))'
                  }}>
                    {section.items.map(renderConfigCard)}
                  </div>
                )}
              </div>
            </TabPane>
          ))}
        </Tabs>
      </Card>

      {/* View Modal */}
      <Modal
        title={
          <Space>
            <EyeOutlined />
            View Configuration: {viewingItem?.section}.{viewingItem?.key}
          </Space>
        }
        open={isViewModalVisible}
        onCancel={() => {
          setIsViewModalVisible(false);
          setViewingItem(null);
        }}
        footer={[
          <Button 
            key="copy" 
            icon={<CopyOutlined />}
            onClick={() => viewingItem && copyToClipboard(JSON.stringify(viewingItem.value, null, 2))}
          >
            Copy
          </Button>,
          <Button key="close" onClick={() => setIsViewModalVisible(false)}>
            Close
          </Button>
        ]}
        width={700}
      >
        {viewingItem && (
          <pre style={{ 
            background: '#f6f8fa',
            padding: 16,
            borderRadius: 6,
            border: '1px solid #e1e4e8',
            fontSize: 13,
            fontFamily: 'Monaco, Consolas, monospace',
            maxHeight: 400,
            overflow: 'auto'
          }}>
            {JSON.stringify(viewingItem.value, null, 2)}
          </pre>
        )}
      </Modal>

      {/* Edit Modal */}
      <Modal
        title={
          <Space>
            <EditOutlined />
            Edit Configuration: {editingItem?.section}.{editingItem?.key}
          </Space>
        }
        open={isModalVisible}
        onOk={handleSaveEdit}
        onCancel={() => {
          setIsModalVisible(false);
          setEditingItem(null);
          form.resetFields();
        }}
        width={700}
        okText="Save Changes"
        confirmLoading={savingKey === `${editingItem?.section}.${editingItem?.key}`}
        okButtonProps={{ icon: <SaveOutlined /> }}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="value"
            label={
              <Space>
                <Text>Value</Text>
                <Tag color={getTypeColor(editingItem?.type || 'string')}>
                  {editingItem?.type}
                </Tag>
              </Space>
            }
            rules={[{ required: true, message: 'Please enter a value' }]}
          >
            {editingItem?.type === 'boolean' ? (
              <Switch 
                checked={form.getFieldValue('value')} 
                onChange={(checked) => form.setFieldsValue({ value: checked })}
              />
            ) : editingItem?.type === 'number' ? (
              <InputNumber style={{ width: '100%' }} />
            ) : (editingItem?.type === 'object' || editingItem?.type === 'array') ? (
              <TextArea 
                rows={12} 
                placeholder="Enter valid JSON"
                style={{ fontFamily: 'Monaco, Consolas, monospace', fontSize: 13 }}
              />
            ) : (
              <Input />
            )}
          </Form.Item>
        </Form>
      </Modal>

      {/* Add Modal */}
      <Modal
        title={
          <Space>
            <PlusOutlined />
            Add Configuration to {activeSection}
          </Space>
        }
        open={isAddModalVisible}
        onOk={handleAdd}
        onCancel={() => {
          setIsAddModalVisible(false);
          addForm.resetFields();
        }}
        width={600}
        okText="Add Configuration"
        okButtonProps={{ icon: <SaveOutlined /> }}
      >
        <Form form={addForm} layout="vertical">
          <Form.Item
            name="key"
            label="Configuration Key"
            rules={[
              { required: true, message: 'Please enter a key' },
              { pattern: /^[a-zA-Z0-9_-]+$/, message: 'Key can only contain letters, numbers, underscores, and hyphens' }
            ]}
          >
            <Input placeholder="e.g. new_database_connection" />
          </Form.Item>
          <Form.Item
            name="type"
            label="Value Type"
            rules={[{ required: true, message: 'Please select a type' }]}
          >
            <Select placeholder="Select the type of value">
              <Select.Option value="string">String</Select.Option>
              <Select.Option value="number">Number</Select.Option>
              <Select.Option value="boolean">Boolean</Select.Option>
              <Select.Option value="object">Object</Select.Option>
              <Select.Option value="array">Array</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="value"
            label="Value"
            rules={[{ required: true, message: 'Please enter a value' }]}
          >
            <TextArea 
              rows={6} 
              placeholder="Enter value (use JSON format for objects/arrays)"
              style={{ fontFamily: 'Monaco, Consolas, monospace', fontSize: 13 }}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ConfigManagementPage;